/**
 * Двуязычие: рантайм.
 *
 * ПОЧЕМУ ПАРА «ru/en» В ОДНОМ ОБЪЕКТЕ, А НЕ КЛЮЧИ И ДВА ФАЙЛА ПЕРЕВОДА.
 * Схема с ключами (`t("overview.kpi.leads")`) даёт две беды, обе из которых уже
 * стоили бы дорого именно этой системе. Первая: из разметки исчезает текст, и
 * подпись показателя перестаёт быть видна там, где она используется, — а у нас
 * подпись несёт оговорку («12,5 % лидов · когда-либо доходили до INTERESTED»),
 * и оговорка обязана читаться рядом с местом, где её печатают. Вторая: пропуск
 * ключа проявляется в рантайме, на экране у заказчика, а не при сборке.
 *
 * Пара в одном объекте, поле в поле, проверяется типом: недостающий перевод НЕ
 * КОМПИЛИРУЕТСЯ. Это уже принятое в проекте решение — так с самого начала
 * сделан справочник показателей (`content/metricsReference.ts`), и там же
 * записана причина: два отдельных дерева расходятся молча, и через месяц
 * русская версия описывает одну систему, а английская — другую.
 *
 * ЧТО НЕ ПЕРЕВОДИТСЯ, И ЭТО РЕШЕНИЕ, А НЕ ПРОПУСК.
 *   • Названия стадий из Битрикса («FOR 1-st APPROACH», «Park Tour», «JUNK
 *     LEAD»). Это зеркало CRM: менеджер видит в Битриксе именно эти слова, и
 *     перевод разорвал бы соответствие — «Осмотр парка» на экране невозможно
 *     сопоставить со «Park Tour» в воронке.
 *   • Имена парков, брендов, менеджеров, названия кампаний, заголовки сделок.
 *     Это данные, а не интерфейс.
 *   • Валюты и коды (AED, BHD) — международные обозначения.
 *
 * ЯЗЫК ОДИН НА ВСЁ ПРИЛОЖЕНИЕ, включая справочник показателей. До этого у
 * справочника был свой переключатель (`lib/docLang.ts`), и получалось, что
 * человек с русским справочником смотрел английские экраны.
 */

import { useSyncExternalStore } from "react";

export type Lang = "ru" | "en";

/** Пара переводов. Оба поля обязательны — на этом стоит вся проверка. */
export type L = { ru: string; en: string };

const KEY = "za_lang";

function initial(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "ru" || saved === "en") return saved;
  } catch {
    /* приватный режим — берём язык браузера */
  }
  return navigator.language?.toLowerCase().startsWith("ru") ? "ru" : "en";
}

/**
 * Язык в модульной переменной, а не только в состоянии React.
 *
 * Нужен синхронный доступ из мест, где хуков нет и быть не может: форматирование
 * чисел и дат (`lib/format.ts`), сборка опций графиков (`lib/chart.ts`),
 * вспомогательные функции вне компонентов. Прокидывать язык параметром в
 * `money()` значило бы поправить около трёхсот вызовов и получить триста мест,
 * где однажды забудут.
 *
 * Согласованность обеспечена тем, что язык у документа один: он меняется целиком
 * и вызывает перерисовку всего дерева (см. `notify`).
 */
let current: Lang = initial();

const listeners = new Set<() => void>();

/** Язык прямо сейчас. Для кода вне компонентов. */
export function currentLang(): Lang {
  return current;
}

/**
 * Выбор языка.
 *
 * Атрибуты на `<html>` ставятся до оповещения слушателей: по `lang` работают
 * переносы и подбор шрифта в браузере, а `useThemeTick` следит именно за
 * атрибутом — так графики перерисовываются тем же механизмом, что и при смене
 * темы, без второго способа делать то же самое.
 */
export function setLang(next: Lang): void {
  if (next === current) return;
  current = next;
  document.documentElement.setAttribute("lang", next);
  document.documentElement.setAttribute("data-lang", next);
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* не сохранилось — не повод ломать экран */
  }
  for (const fn of listeners) fn();
}

/** Ставит атрибуты при первой загрузке. Зовётся один раз, из `main.tsx`. */
export function applyInitialLang(): void {
  document.documentElement.setAttribute("lang", current);
  document.documentElement.setAttribute("data-lang", current);
}

/**
 * Подписка на язык для React.
 *
 * Зовётся ОДИН РАЗ, в корневом компоненте: смена языка перерисовывает всё
 * дерево, потому что язык у документа один и меняется целиком. Подписывать
 * каждый компонент отдельно значило бы сотню подписок ради одного события,
 * и любой пропущенный компонент остался бы на прежнем языке — а полупереведённый
 * экран выглядит как поломка, а не как настройка.
 */
export function useLang(): Lang {
  return useSyncExternalStore(subscribeLang, currentLang, currentLang);
}

export function subscribeLang(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Выбирает перевод.
 *
 * Имя из одной буквы — намеренно: вызов встречается больше тысячи раз, и
 * `{t(LABEL)}` в разметке читается, а `{translate(LABEL)}` превращает строку
 * подписи в строку про перевод.
 */
export function t(pair: L): string {
  return pair[current];
}

/**
 * Подпись, которая приезжает С СЕРВЕРА, по её ключу.
 *
 * Так устроено двуязычие всего, что база отдаёт словами: сервер присылает
 * стабильный КЛЮЧ, а слова — дело интерфейса. Причина в том, что иначе
 * двуязычными пришлось бы делать сами представления и таблицы-справочники: по
 * колонке на язык, и каждое новое поле — миграция плюс риск, что одна из двух
 * колонок останется пустой и на экране окажется дырка вместо подписи.
 *
 * `fallback` — то, что прислал сервер. Неизвестный ключ (новый источник, новый
 * вид проблемы) даёт английскую подпись из базы, а не пустоту: неполный перевод
 * лучше отсутствующей подписи, и на экране сразу видно, что переводить.
 */
export function dbLabel(
  dict: Record<string, L>,
  key: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const pair = key === null || key === undefined ? undefined : dict[key];
  if (pair !== undefined) return pair[current];
  return fallback ?? key ?? "";
}

/**
 * Русский требует трёх форм: 1 лид, 2 лида, 5 лидов.
 *
 * Английское «1 lead / 2 leads» покрывается двумя формами, поэтому у английского
 * третья равна второй. Правило стандартное для русского, а не самодельное: на
 * 11–14 — форма множественного числа, на 1 (кроме 11) — единственное.
 */
export function plural(n: number, forms: { one: L; few: L; many: L }): string {
  if (current === "en") return t(Math.abs(n) === 1 ? forms.one : forms.many);
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return t(forms.many);
  if (last === 1) return t(forms.one);
  if (last >= 2 && last <= 4) return t(forms.few);
  return t(forms.many);
}
